// Fixed Gift Wrapping Script
class GiftWrapManager {
  constructor() {
    this.observer = null;
    this.init();
  }

  init() {
    // Bind initial checkbox if it exists
    this.bindGiftWrapCheckbox();
    
    // Set up MutationObserver to watch for AJAX cart updates
    this.setupObserver();
  }

  bindGiftWrapCheckbox() {
    const checkbox = document.querySelector('#gift-wrap-checkbox');
    
    if (checkbox) {
      // Remove existing listener to prevent duplicates
      checkbox.removeEventListener('change', this.handleCheckboxChange);
      // Add fresh listener
      checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
    }
  }

  async handleCheckboxChange(event) {
    const checkbox = event.target;
    const isChecked = checkbox.checked;
    const giftWrapVariantId = checkbox.dataset.variantId;
    const giftWrapLine = checkbox.dataset.line;
    const cartTotalItems = parseInt(checkbox.dataset.cartTotalItems) || 0;
    
    if (!giftWrapVariantId) {
      console.error('No variant ID found on checkbox!');
      return;
    }
    
    // Check if cart has items before adding gift wrap
    if (cartTotalItems === 0 && isChecked) {
      alert("Add some products to your cart first before selecting gift wrap.");
      checkbox.checked = false;
      return;
    }
    
    // Disable checkbox during request
    checkbox.disabled = true;
    
    try {
      let response;
      let requestData;
      
      if (isChecked) {
        // Add gift wrap
        requestData = {
          id: giftWrapVariantId,
          quantity: cartTotalItems
        };
        
        response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData)
        });
      } else {
        // Remove gift wrap
        if (giftWrapLine && giftWrapLine > 0) {
          requestData = {
            line: giftWrapLine,
            quantity: 0
          };
          
          response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData)
          });
        } else {
          requestData = {
            id: giftWrapVariantId,
            quantity: 0
          };
          
          response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestData)
          });
        }
      }
      
      if (response.ok) {
        // Refresh cart display
        this.refreshCartDisplay();
      } else {
        throw new Error('Failed to update cart');
      }
    } catch (error) {
      console.error('Gift wrap update failed:', error);
      // Revert checkbox state on error
      checkbox.checked = !isChecked;
    } finally {
      // Re-enable checkbox
      checkbox.disabled = false;
    }
  }

  refreshCartDisplay() {
    // Check if we're on the main cart page
    if (window.location.pathname.includes('/cart')) {
      // Reload the page for main cart
      window.location.reload();
    } else {
      // Refresh AJAX cart for side drawer
      this.refreshAjaxCart();
    }
  }

  refreshAjaxCart() {
    // Method 1: Try theme-specific cart refresh functions
    if (this.tryThemeSpecificRefresh()) {
      return;
    }
    
    // Method 2: Force close and reopen cart drawer (most reliable)
    this.closeAndReopenCartDrawer();
  }

  tryThemeSpecificRefresh() {
    // Try common theme-specific refresh methods
    const refreshMethods = [
      // Dawn theme
      () => window.CartDrawer && window.CartDrawer.getSectionsToRender && window.CartDrawer.getSectionsToRender(),
      () => window.CartDrawer && window.CartDrawer.renderContents && window.CartDrawer.renderContents(),
      
      // Theme with cart drawer element
      () => {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents();
          return true;
        }
        return false;
      },
      
      // Shopify Ajax Cart
      () => window.ajaxCart && window.ajaxCart.load && window.ajaxCart.load(),
      
      // Theme object methods
      () => window.theme && window.theme.cart && window.theme.cart.refresh && window.theme.cart.refresh(),
      () => window.theme && window.theme.CartDrawer && window.theme.CartDrawer.refresh && window.theme.CartDrawer.refresh(),
      
      // Other common methods
      () => window.updateCartDrawer && window.updateCartDrawer(),
      () => window.refreshCart && window.refreshCart(),
      () => window.updateCart && window.updateCart(),
      () => window.cartUpdateCallback && window.cartUpdateCallback(),
      
      // Timber/Slate theme
      () => window.timber && window.timber.CartDrawer && window.timber.CartDrawer.build && window.timber.CartDrawer.build(),
      
      // Section refresh (for themes using sections)
      () => {
        if (window.Shopify && window.Shopify.theme && window.Shopify.theme.sections) {
          const cartSection = document.querySelector('[data-section-type="cart-drawer"]');
          if (cartSection) {
            const sectionId = cartSection.getAttribute('data-section-id');
            if (sectionId) {
              fetch(`/cart?section_id=${sectionId}`)
                .then(response => response.text())
                .then(html => {
                  cartSection.innerHTML = html;
                  this.bindGiftWrapCheckbox();
                });
              return true;
            }
          }
        }
        return false;
      }
    ];
    
    for (const method of refreshMethods) {
      try {
        if (method()) {
          console.log('Successfully called theme-specific refresh method');
          // Rebind checkbox after refresh
          setTimeout(() => this.bindGiftWrapCheckbox(), 200);
          return true;
        }
      } catch (error) {
        console.log('Theme method failed:', error);
        continue;
      }
    }
    
    return false;
  }

  forceRefreshCartDrawer() {
    // Find the cart drawer
    const possibleSelectors = [
      '[data-cart-drawer]',
      '.cart-drawer',
      '#cart-drawer',
      '.mini-cart',
      '.slideout',
      '[data-wau-slideout="ajax-cart"]',
      'cart-drawer'
    ];
    
    let cartDrawer = null;
    
    for (const selector of possibleSelectors) {
      cartDrawer = document.querySelector(selector);
      if (cartDrawer) break;
    }
    
    if (!cartDrawer) {
      console.log('No cart drawer found, reloading page');
      window.location.reload();
      return;
    }
    
    // Method 1: Try to find and fetch the cart drawer's specific template
    const cartDrawerInner = cartDrawer.querySelector('.cart-drawer__inner') || 
                           cartDrawer.querySelector('.cart-drawer-content') ||
                           cartDrawer.querySelector('.mini-cart-content') ||
                           cartDrawer;
    
    // Fetch the cart drawer HTML with a section-specific URL
    fetch('/cart')
      .then(response => response.text())
      .then(html => {
        // Create a temporary element to parse the response
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        
        // Try to find the gift wrap container in the new HTML
        const newGiftWrapContainer = tempDiv.querySelector('#gift-wrap-container');
        
        if (newGiftWrapContainer) {
          // Update just the gift wrap container
          const currentGiftWrapContainer = document.querySelector('#gift-wrap-container');
          if (currentGiftWrapContainer) {
            currentGiftWrapContainer.outerHTML = newGiftWrapContainer.outerHTML;
            // Re-bind the checkbox after updating
            setTimeout(() => this.bindGiftWrapCheckbox(), 50);
          }
        }
        
        // Update cart totals and counts by finding them in the new HTML
        const newCartTotal = tempDiv.querySelector('[data-cart-total]') || 
                           tempDiv.querySelector('.cart-total') ||
                           tempDiv.querySelector('.cart__total');
        
        if (newCartTotal) {
          const currentCartTotals = document.querySelectorAll('[data-cart-total], .cart-total, .cart__total');
          currentCartTotals.forEach(element => {
            element.innerHTML = newCartTotal.innerHTML;
          });
        }
        
        // Update cart item count
        const newCartCount = tempDiv.querySelector('[data-cart-count]') || 
                           tempDiv.querySelector('.cart-count');
        
        if (newCartCount) {
          const currentCartCounts = document.querySelectorAll('[data-cart-count], .cart-count');
          currentCartCounts.forEach(element => {
            element.textContent = newCartCount.textContent;
          });
        }
        
        // Update cart items list
        const newCartItems = tempDiv.querySelector('.cart-items') || 
                            tempDiv.querySelector('.cart__items') ||
                            tempDiv.querySelector('.mini-cart__items');
        
        if (newCartItems) {
          const currentCartItems = document.querySelector('.cart-items') || 
                                 document.querySelector('.cart__items') ||
                                 document.querySelector('.mini-cart__items');
          
          if (currentCartItems) {
            currentCartItems.innerHTML = newCartItems.innerHTML;
          }
        }
        
        // Dispatch events to let theme know cart was updated
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      })
      .catch(error => {
        console.error('Failed to refresh cart drawer:', error);
        // Fallback: close and reopen cart drawer to force refresh
        this.closeAndReopenCartDrawer();
      });
  }

  closeAndReopenCartDrawer() {
    // Try to find cart drawer toggle button
    const cartToggleSelectors = [
      '[data-cart-drawer-toggle]',
      '.cart-drawer-toggle',
      '.cart-toggle',
      '.mini-cart-toggle',
      '.cart-link',
      '[aria-controls*="cart"]',
      '.header__icon--cart',
      '.cart-count-bubble',
      '[data-cart-count]',
      '.js-drawer-open-right',
      '.drawer-trigger'
    ];
    
    let cartToggle = null;
    
    for (const selector of cartToggleSelectors) {
      const elements = document.querySelectorAll(selector);
      for (const element of elements) {
        // Check if this element actually opens the cart
        if (element.getAttribute('aria-controls') || 
            element.getAttribute('data-cart-drawer-toggle') !== null ||
            element.classList.contains('cart-toggle') ||
            element.classList.contains('cart-link')) {
          cartToggle = element;
          break;
        }
      }
      if (cartToggle) break;
    }
    
    if (cartToggle) {
      console.log('Found cart toggle:', cartToggle);
      
      // Close the drawer first
      const cartDrawer = document.querySelector('[data-cart-drawer], .cart-drawer, #cart-drawer, .mini-cart, .slideout');
      if (cartDrawer) {
        // Try to close via close button first
        const closeButton = cartDrawer.querySelector('[data-cart-drawer-close], .cart-drawer-close, .drawer-close, .close');
        if (closeButton) {
          closeButton.click();
        } else {
          // Try to close by clicking the toggle if it's already open
          cartToggle.click();
        }
      }
      
      // Wait a moment then reopen to get fresh content
      setTimeout(() => {
        cartToggle.click();
        
        // After reopening, rebind the checkbox
        setTimeout(() => {
          this.bindGiftWrapCheckbox();
        }, 500);
      }, 300);
    } else {
      console.log('No cart toggle found, trying alternative refresh methods');
      // Alternative: try to refresh by dispatching events
      this.tryEventBasedRefresh();
    }
  }

  tryEventBasedRefresh() {
    // Try dispatching various events that themes might listen to
    const events = [
      'cart:updated',
      'cart:refresh',
      'cart:build',
      'drawer:refresh',
      'ajaxCart:updated',
      'theme:cart:updated'
    ];
    
    events.forEach(eventName => {
      document.dispatchEvent(new CustomEvent(eventName));
    });
    
    // Also try with a slight delay
    setTimeout(() => {
      events.forEach(eventName => {
        document.dispatchEvent(new CustomEvent(eventName));
      });
    }, 100);
    
    // If all else fails, reload the page
    setTimeout(() => {
      if (!window.location.pathname.includes('/cart')) {
        console.log('Fallback: reloading page');
        window.location.reload();
      }
    }, 1000);
  }

  setupObserver() {
    // Try to find cart drawer container
    const possibleSelectors = [
      '[data-cart-drawer]',
      '.cart-drawer',
      '#cart-drawer',
      '.mini-cart',
      '.slideout',
      '[data-wau-slideout="ajax-cart"]',
      'cart-drawer'
    ];
    
    let cartDrawerContainer = null;
    
    for (const selector of possibleSelectors) {
      cartDrawerContainer = document.querySelector(selector);
      if (cartDrawerContainer) break;
    }
    
    if (!cartDrawerContainer) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          const hasGiftWrapCheckbox = Array.from(mutation.addedNodes).some(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              return node.querySelector('#gift-wrap-checkbox') || node.id === 'gift-wrap-checkbox';
            }
            return false;
          });
          
          if (hasGiftWrapCheckbox) {
            // Delay slightly to ensure DOM is fully updated
            setTimeout(() => {
              this.bindGiftWrapCheckbox();
            }, 100);
          }
        }
      });
    });

    this.observer.observe(cartDrawerContainer, {
      childList: true,
      subtree: true
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.giftWrapManager = new GiftWrapManager();
});

// Initialize immediately if DOM is already loaded
if (document.readyState !== 'loading') {
  window.giftWrapManager = new GiftWrapManager();
}